import type { Keyframe } from '../types';

export type StateTarget =
  | {
      kind: 'keyframe';
      /** DB keyframe id; `Keyframe.id` sayı değilse null (yalnızca layerId + frame ile client güncellemesi). */
      keyframeId: number | null;
      frame: number;
      layerId: string;
      keyframe: Keyframe;
    }
  | {
      kind: 'default';
      layerId: string;
    };

export interface ResolveStateTargetParams {
  layerId: string;
  currentFrame: number;
  /** Bu layer için keyframe listesi (store’daki `keyframes[layerId]`). */
  layerKeyframes: Keyframe[];
  /**
   * Timeline’da keyframe seçiliyse önce bu hedef denenir (liste içinde yoksa cursor kuralına düşülür).
   */
  selectedKeyframe?: { layerId: string; frame: number } | null;
}

function parseKeyframeId(id: string): number | null {
  const n = Number.parseInt(id, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Düzenleme yapılırken state’in yazılacağı hedef: keyframe satırı veya default state (keyframe yok).
 *
 * Kural (eski `keyframesAtCursor` ile uyumlu):
 * 1. `selectedKeyframe` bu layer’a ait ve listede varsa → o keyframe.
 * 2. Cursor karesinde keyframe varsa → o keyframe.
 * 3. Cursor’dan küçük en büyük `frame` (hemen önceki keyframe) → o keyframe.
 * 4. Hiç keyframe yoksa → `default` (sunucuda `keyFrameId === 0`).
 */
export function resolveStateTarget({
  layerId,
  currentFrame,
  layerKeyframes,
  selectedKeyframe,
}: ResolveStateTargetParams): StateTarget {
  const list = layerKeyframes ?? [];

  if (selectedKeyframe?.layerId === layerId) {
    const bySelection = list.find((k) => k.frame === selectedKeyframe.frame);
    if (bySelection) {
      return {
        kind: 'keyframe',
        keyframeId: parseKeyframeId(bySelection.id),
        frame: bySelection.frame,
        layerId,
        keyframe: bySelection,
      };
    }
  }

  const atCursor = list.find((k) => k.frame === currentFrame);
  if (atCursor) {
    return {
      kind: 'keyframe',
      keyframeId: parseKeyframeId(atCursor.id),
      frame: atCursor.frame,
      layerId,
      keyframe: atCursor,
    };
  }

  const before = list
    .filter((k) => k.frame < currentFrame)
    .sort((a, b) => b.frame - a.frame)[0];

  if (before) {
    return {
      kind: 'keyframe',
      keyframeId: parseKeyframeId(before.id),
      frame: before.frame,
      layerId,
      keyframe: before,
    };
  }

  return { kind: 'default', layerId };
}
